import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'

import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Grid,
  Stack,
} from '@mui/material'

import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'

import chatHook from '@hooks/useChat'
import errorHandlerHook from '@hooks/useErrorHandler'
import responsiveHook from '@hooks/useResponsive'
import titleHook from '@hooks/useTitle'
import updateEffectHook from '@hooks/useUpdateEffect'

import PressPostService from '@services/press-post'

import { MatchesCountSection } from '@components/MatchesCountSection'
import { SearchBar, SearchBarVariant } from '@components/SearchBar'
import { TLGTypography } from '@components/TLGTypography'

import { getFilterFields } from '@utils/filter'
import { scrollToTop } from '@utils/scroll'
import { IApiData, IPressPost, IQueryFilters } from '@utils/types'
import { buildQueryString } from '@utils/url'

import {
  buttonsWrapperSx,
  contentGridSx,
  contentSx,
  headerGridSx,
  headerSidebarSx,
  loadingWrapperSx,
  sidebarDividerSx,
  sidebarSx,
  sidebarWrapperSx,
} from './styles'

import { ScheduleADemo } from '../components/ScheduleADemo'
import { PressPost } from './components/PressPost'
import { RecentPressPosts } from './components/RecentPressPosts'

const DEFAULT_PAGE_SIZE = 14

const Press = () => {
  const { t } = useTranslation()
  const { handleError } = errorHandlerHook.useErrorHandler()
  const { isMobileOrTablet } = responsiveHook.useResponsive()
  const { setIsShowAssistanceChatBubble } = chatHook.useChat()

  titleHook.useTitle(t('Technology Match - Press'))

  useEffect(() => {
    setIsShowAssistanceChatBubble(true)
  }, [setIsShowAssistanceChatBubble])

  const [isLoading, setIsLoading] = useState(true)
  const [data, setData] = useState<IApiData<IPressPost[]>>({
    data: [],
    count: 0,
  })

  const pressSectionName = !isMobileOrTablet ? 'press-sidebar' : 'press-bottom'

  const pressPosts = data.data

  const [searchParams, setSearchParams] = useSearchParams()
  const getInitialFilters = useCallback(() => {
    const searchParamPage = searchParams.get('page')
    const page = searchParamPage ? Number(searchParamPage) : 1
    const searchParamLimit = searchParams.get('limit')
    const limit = searchParamLimit
      ? Number(searchParamLimit)
      : DEFAULT_PAGE_SIZE

    const initialFilters: IQueryFilters = {
      page,
      limit,
      filters: getFilterFields({ searchParams }),
    }

    return initialFilters
  }, [searchParams])

  const [filters, setFilters] = useState<IQueryFilters>(() =>
    getInitialFilters()
  )

  const fetchPressPosts = useCallback(async () => {
    scrollToTop()
    setIsLoading(true)

    try {
      const response = await PressPostService.findAllAsVisitor(filters)
      setData(response)
    } catch (err) {
      handleError({ exception: err })
    }

    setIsLoading(false)
  }, [filters, handleError])

  useEffect(() => {
    setFilters(getInitialFilters())
  }, [getInitialFilters, searchParams])

  updateEffectHook.useUpdateEffect(() => {
    fetchPressPosts()
  }, [filters])

  const handleOnPageChange = (newPage: number) => {
    const page = newPage > 1 ? newPage : undefined
    const limit =
      filters?.limit !== DEFAULT_PAGE_SIZE ? filters.limit : undefined

    const queryString =
      buildQueryString({
        ...filters,
        page,
        limit,
      }) ?? {}

    setSearchParams(queryString)
  }

  const handleOnSearch = (searchText: string) => {
    const filterFields =
      filters.filters?.filter((x) => x.field !== 'search') ?? []

    if (searchText?.length > 0) {
      filterFields.push({ field: 'search', value: searchText })
    }

    const queryString =
      buildQueryString({
        page: undefined,
        limit: undefined,
        filters: filterFields,
      }) ?? {}

    setSearchParams(queryString)
  }

  const handleOnClear = () => {
    handleOnSearch('')
  }

  const isPressPostsEmpty = pressPosts.length === 0

  return (
    <Stack sx={contentSx}>
      <Box
        display="grid"
        gridTemplateColumns="repeat(26, 1fr)"
        sx={headerGridSx}
      >
        <Box gridColumn={{ xs: 'span 26', lg: 'span 15', xl: 'span 18' }}>
          <TLGTypography
            responsiveVariant={{ xs: 'h4', md: 'h3' }}
            component="h1"
          >
            {t('Press')}
          </TLGTypography>
        </Box>
        <Stack
          gridColumn={{ xs: 'span 26', lg: 'span 9 / 27', xl: 'span 6 / 27' }}
          sx={headerSidebarSx}
        >
          <SearchBar
            variant={SearchBarVariant.LANDING_PAGE}
            placeholder="Search Press"
            onSearch={handleOnSearch}
            initialValue={searchParams.get('search') ?? ''}
            onClear={handleOnClear}
            maxLength={128}
          />
        </Stack>
      </Box>

      <Divider />
      <Box
        display="grid"
        gridTemplateColumns="repeat(26, 1fr)"
        sx={contentGridSx}
      >
        <Box gridColumn={{ xs: 'span 26', lg: 'span 15', xl: 'span 18' }}>
          {isLoading ? (
            <Stack sx={loadingWrapperSx}>
              <CircularProgress />
            </Stack>
          ) : (
            <>
              {isPressPostsEmpty ? (
                <TLGTypography variant="h6" component="h3">
                  {t('No press posts found')}
                </TLGTypography>
              ) : (
                <Grid
                  container
                  rowSpacing={{ xs: '2.4rem', lg: '6rem' }}
                  columnSpacing={{ xs: '1.6rem', lg: '2.4rem' }}
                >
                  {pressPosts.map((pressPost) => (
                    <Grid key={pressPost.externalUrl} item xs={12} md={6}>
                      <PressPost
                        imageUrl={pressPost.image as string}
                        title={pressPost.title}
                        url={pressPost.externalUrl}
                      />
                    </Grid>
                  ))}
                  <Grid item xs={12}>
                    <Stack sx={buttonsWrapperSx}>
                      <Button
                        data-testid="older-button"
                        variant="outlined"
                        size="medium"
                        startIcon={<ChevronLeftIcon />}
                        onClick={() => handleOnPageChange(filters.page + 1)}
                        disabled={filters.page * filters.limit >= data.count}
                      >
                        {t('Older')}
                      </Button>
                      <Button
                        data-testid="newer-button"
                        variant="outlined"
                        size="medium"
                        endIcon={<ChevronRightIcon />}
                        onClick={() => handleOnPageChange(filters.page - 1)}
                        disabled={filters.page === 1}
                      >
                        {t('Newer')}
                      </Button>
                    </Stack>
                  </Grid>
                </Grid>
              )}
            </>
          )}
        </Box>
        <Box
          gridColumn={{ xs: 'span 26', lg: 'span 9 / 27', xl: 'span 6 / 27' }}
          sx={sidebarSx}
        >
          <Stack data-testid={pressSectionName} sx={sidebarWrapperSx}>
            {isMobileOrTablet && <Divider sx={sidebarDividerSx} />}
            <RecentPressPosts />
            <ScheduleADemo />
            <MatchesCountSection variant="sidebar" />
          </Stack>
        </Box>
      </Box>
    </Stack>
  )
}

export { Press }
